import React from 'react';
import { Pressable, PressableProps, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';

import colors, { appTheme } from '@/config/colors';
import { Icon, IconName } from './Icon';

interface ButtonProps extends PressableProps {
  label: string;
  icon?: IconName;
  look?: keyof typeof looks;
}

const looks = {
  minimal: {
    wrapper: {
      backgroundColor: 'transparent',
    },
    text: {
      color: appTheme.textColor,
    },
  },
  default: {
    wrapper: {},
    text: {},
  },
  danger: {
    wrapper: {
      backgroundColor: colors.red,
    },
    text: {
      color: colors.white,
    },
  },
  dangerOutlined: {
    wrapper: {
      borderWidth: 1,
      borderColor: colors.red,
      backgroundColor: 'transparent',
    },
    text: {
      color: colors.red,
    },
  },
  neutral: {
    wrapper: {
      backgroundColor: colors.twGrey,
    },
    text: {
      color: appTheme.textColor,
    },
  },
  secondary: {
    wrapper: {
      backgroundColor: colors.white,
      borderWidth: 1,
      borderColor: appTheme.primary,
    },
    text: {
      color: appTheme.primary,
    },
  },
};

const smallButtonStyles = StyleSheet.create({
  wrapper: {
    height: 36,
    paddingHorizontal: 16,
    backgroundColor: appTheme.primary,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 24,
    flexDirection: 'row',
    columnGap: 4,
  },
  icon: {
    fontSize: 14,
    color: colors.white,
  },
  label: {
    fontWeight: '600',
    fontSize: 14,
    color: colors.white,
  },
});

export function SmallButton({
  label,
  icon,
  look = 'default',
  ...props
}: ButtonProps) {
  const iconStyle = {
    ...smallButtonStyles.icon,
    ...looks[look].text,
  };

  return (
    <Pressable
      {...props}
      style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
    >
      <View style={[smallButtonStyles.wrapper, looks[look].wrapper]}>
        {icon ? <Icon style={iconStyle} name={icon} /> : null}
        <Text style={[smallButtonStyles.label, looks[look].text]}>{label}</Text>
      </View>
    </Pressable>
  );
}

const primaryButtonStyles = StyleSheet.create({
  wrapper: {
    height: 48,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    flexDirection: 'row',
    columnGap: 8,
    backgroundColor: appTheme.primary,
  },
  icon: {
    fontSize: 24,
    color: colors.white,
  },
  label: {
    fontWeight: '600',
    fontSize: 14,
    color: colors.white,
  },
});

export function PrimaryButton({
  label,
  look = 'default',
  icon,
  ...props
}: ButtonProps) {
  const iconStyle = {
    ...primaryButtonStyles.icon,
    ...looks[look].text,
  };

  return (
    <Pressable
      {...props}
      style={({ pressed }) => [
        { opacity: pressed ? 0.7 : 1 },
        props.disabled ? { opacity: 0.5, backgroundColor: colors.grey } : {},
      ]}
    >
      <View style={[primaryButtonStyles.wrapper, looks[look].wrapper]}>
        {icon ? <Icon style={iconStyle} name={icon} /> : null}
        <Text style={[primaryButtonStyles.label, , looks[look].text]}>
          {label}
        </Text>
      </View>
    </Pressable>
  );
}

const iconButtonStyles = StyleSheet.create({
  wrapper: {
    height: 48,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    color: colors.black,
    fontSize: 22,
  },
  label: {
    marginTop: 4,
    fontWeight: '500',
    fontSize: 12,
  },
});

export function IconButton({
  label,
  icon,
  ...props
}: PressableProps & { label: string; icon: IconName }) {
  return (
    <Pressable
      {...props}
      style={({ pressed }) => [
        {
          borderRadius: 14,
          backgroundColor: pressed ? colors.primaryBrighter : 'transparent',
          opacity: pressed ? 0.7 : 1,
        },
      ]}
    >
      <View style={iconButtonStyles.wrapper}>
        <Icon style={iconButtonStyles.icon} name={icon} />
        {!!label && <Text style={iconButtonStyles.label}>{label}</Text>}
      </View>
    </Pressable>
  );
}
